import type {
  ChessboardActionLogDto,
  ChessboardActionPayload,
  ChessboardDto,
  ChessMoveDto,
  CreateChessboardPayload,
  MakeMovePayload,
  MyBoardsDto,
  VettedLocationDto,
} from '@/core/data/chessboard/chessboard.interface';
import type { ChessboardRepository } from '@/core/data/chessboard/chessboard.repository';
import type { ApiError, Result } from '@/core/network/http/httpClient.interface';
import { generalError } from '@/core/network/http/httpError';

export class ChessboardService {
  private readonly repository: ChessboardRepository;

  constructor(repository: ChessboardRepository) {
    this.repository = repository;
  }

  async getBoard(id: string): Promise<Result<ChessboardDto>> {
    try {
      const response = await this.repository.getBoard(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getMyBoards(): Promise<Result<MyBoardsDto>> {
    try {
      const response = await this.repository.getMyBoards();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getActiveByLocation(locationId: string): Promise<Result<ChessboardDto[]>> {
    try {
      const response = await this.repository.getActiveByLocation(locationId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getCompletedByLocation(locationId: string): Promise<Result<ChessboardDto[]>> {
    try {
      const response = await this.repository.getCompletedByLocation(locationId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getMoves(chessboardId: string): Promise<Result<ChessMoveDto[]>> {
    try {
      const response = await this.repository.getMoves(chessboardId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getActionLogs(chessboardId: string): Promise<Result<ChessboardActionLogDto[]>> {
    try {
      const response = await this.repository.getActionLogs(chessboardId);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getWorldBoards(): Promise<Result<ChessboardDto[]>> {
    try {
      const response = await this.repository.getWorldBoards();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getAllBoards(): Promise<Result<ChessboardDto[]>> {
    try {
      const response = await this.repository.getAllBoards();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async getVettedLocations(): Promise<Result<VettedLocationDto[]>> {
    try {
      const response = await this.repository.getVettedLocations();
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async create(payload: CreateChessboardPayload): Promise<Result<ChessboardDto>> {
    try {
      const response = await this.repository.create(payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async makeMove(id: string, payload: MakeMovePayload): Promise<Result<ChessboardDto>> {
    try {
      const response = await this.repository.makeMove(id, payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async performAction(id: string, payload: ChessboardActionPayload): Promise<Result<ChessboardDto>> {
    try {
      const response = await this.repository.performAction(id, payload);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async createFirestoreDoc(id: string): Promise<Result<any>> {
    try {
      const response = await this.repository.createFirestoreDoc(id);
      return response?.data ? response : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async abandon(id: string): Promise<Result<ChessboardDto>> {
    try {
      const response = await this.repository.abandon(id);
      return response?.data ? { data: response.data } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }

  async deleteBoard(id: string): Promise<Result<boolean>> {
    try {
      const deleted = await this.repository.deleteBoard(id);
      return deleted ? { data: true } : { error: generalError() };
    } catch (error) {
      return { error: error as ApiError };
    }
  }
}
